import { AfterViewInit, Component, ElementRef, EventEmitter, Injectable, Input, NgModule, NgZone, OnDestroy, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationStart } from '@angular/router';
import { Subscription } from 'rxjs';

interface GoogleIdentity {
  initialize(options: { client_id: string; callback: (response: { credential?: string }) => void; auto_select: boolean; ux_mode: string }): void;
  renderButton(parent: HTMLElement, options: object): void;
}

// A single GIS configuration is shared by all website entry points. No native plugin.
@Injectable({ providedIn: 'root' })
export class GoogleIdentityService {
  private ready?: Promise<GoogleIdentity>;
  private receiver?: (token?: string) => void;

  constructor(private zone: NgZone, router: Router) {
    router.events.subscribe(event => {
      if (event instanceof NavigationStart) { this.receiver = undefined; }
    });
  }

  load(): Promise<GoogleIdentity> {
    if (this.ready) { return this.ready; }
    this.ready = new Promise<GoogleIdentity>((resolve, reject) => {
      const clientId = document.querySelector<HTMLMetaElement>('meta[name="rovespier-google-client-id"]')?.content;
      if (!clientId) { reject(new Error('Google client configuration is missing.')); return; }
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      let settled = false;
      const fail = () => {
        if (settled) { return; }
        settled = true;
        clearTimeout(timeout);
        script.remove();
        reject(new Error('Google sign-in could not load.'));
      };
      const timeout = window.setTimeout(fail, 15000);
      script.onerror = fail;
      script.onload = () => {
        if (settled) { return; }
        const api: GoogleIdentity | undefined = (window as any).google?.accounts?.id;
        if (!api) { fail(); return; }
        try {
          api.initialize({ client_id: clientId, auto_select: false, ux_mode: 'popup', callback: response => {
            const receive = this.receiver;
            this.receiver = undefined;
            this.zone.run(() => receive?.(response?.credential));
          }});
          settled = true;
          clearTimeout(timeout);
          resolve(api);
        } catch { fail(); }
      };
      document.head.appendChild(script);
    }).catch(error => { this.ready = undefined; throw error; });
    return this.ready;
  }

  select(receiver: (token?: string) => void) { this.receiver = receiver; }
  release(receiver: (token?: string) => void) {
    if (this.receiver === receiver) { this.receiver = undefined; }
  }
}

@Component({
  selector: 'rv-google-signin',
  template: `<div #buttonHost [hidden]="busy" class="google-button"></div>
    <p *ngIf="busy" role="status">Signing in…</p>
    <p *ngIf="error" role="alert">{{ error }} <button type="button" (click)="render()">Retry</button></p>`,
  styles: [`:host { display: block; min-height: 44px; margin: 8px 0; }
    .google-button { display: flex; justify-content: center; }
    .google-button[hidden] { display: none; }
    p { margin: 8px 0; text-align: center; font-size: 14px; }
    button { background: transparent; color: inherit; text-decoration: underline; cursor: pointer; }`]
})
export class GoogleSigninComponent implements AfterViewInit, OnDestroy {
  @Input() busy = false;
  @Output() credential = new EventEmitter<string>();
  @ViewChild('buttonHost', { static: true }) buttonHost!: ElementRef<HTMLElement>;
  error = '';
  private destroyed = false;
  private navigation: Subscription;
  private receive = (token?: string) => {
    if (this.destroyed || this.busy || this.buttonHost.nativeElement.closest('.ion-page-hidden')) { return; }
    // Shape validation only. The PHP endpoint must verify Google's signature and claims.
    if (typeof token !== 'string' || !/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(token)) {
      this.error = 'Google did not return a valid sign-in credential.';
      return;
    }
    this.credential.emit(token);
  };

  constructor(private identity: GoogleIdentityService, private zone: NgZone, router: Router) {
    this.navigation = router.events.subscribe(event => {
      if (event instanceof NavigationStart) { this.identity.release(this.receive); }
    });
  }
  ngAfterViewInit() { this.render(); }
  async render() {
    this.error = '';
    try {
      const api = await this.identity.load();
      if (this.destroyed) { return; }
      const host = this.buttonHost.nativeElement;
      host.textContent = '';
      api.renderButton(host, { type: 'standard', theme: 'outline', size: 'large',
        text: 'continue_with', shape: 'rectangular', logo_alignment: 'left',
        width: Math.min(400, Math.max(200, host.parentElement?.clientWidth || 280)),
        click_listener: () => {
          if (!this.busy && !this.destroyed) { this.identity.select(this.receive); }
        }
      });
    } catch {
      if (!this.destroyed) {
        this.zone.run(() => { this.error = 'Google sign-in is unavailable. Check your connection and try again.'; });
      }
    }
  }
  ngOnDestroy() {
    this.destroyed = true;
    this.identity.release(this.receive);
    this.navigation.unsubscribe();
  }
}

@NgModule({ declarations: [GoogleSigninComponent], imports: [CommonModule], exports: [GoogleSigninComponent] })
export class GoogleSigninModule {}
